/**
 * Fast Simon — upsell & cross-sell recommendations controller (product-page widgets).
 *
 * Fast Simon owns WHICH products a recommendation widget shows; Shopify Liquid still renders
 * the cards. For every widget we ask Fast Simon for recommendations
 * (`window.FastSimonSDK.productRecommendationByWidget`), then request the theme's native
 * `_product-card` markup for the returned handles through `sections/fs-search-cards.liquid`
 * — the same server-side card renderer the search/collection surfaces use (see
 * `assets/fs-sdk-results.js`). The widget therefore carries the exact card design configured
 * in the theme editor, and any change to the card follows without touching this file.
 *
 * Surfaces: every `<fs-recommendations>` element on the page — one per widget. Two elements
 * render on a product page: the "Product recommendations" section (related products) and the
 * same block inside the product details ("Styled with" complementary products). The section
 * and block each render the script tag below, so this file also re-runs when the theme editor
 * re-renders them; every entry point funnels into `scan()`, which mounts only elements that
 * are not mounted yet. The element carries everything the controller needs:
 *
 *   data-source-product-id  the product page's product (numeric id)
 *   data-widget-ids         comma separated Fast Simon widget ids from the dashboard
 *   data-max-products       cap on rendered products (theme editor "product count" setting)
 *
 * The widget chrome (heading blocks, color scheme, spacing, grid/carousel layout, the
 * loading skeleton) stays in Liquid; this file only fills the list and removes the skeleton.
 *
 * Boot: the SDK is only guaranteed to behave from its `onReady` (initialization finished)
 * onward, so the controller waits for `window.__fastSimonSdkReady` — set by the theme's
 * onReady in layout/theme.liquid, with a chained-onReady fallback and a bounded poll below
 * — never for the bare `window.FastSimonSDK` symbol, which exists earlier. The script is
 * rendered AFTER its <fs-recommendations> element, and every start rescans at
 * DOMContentLoaded, because a parser-inserted script executes mid-parse — before the
 * element it serves exists — and an immediate scan would otherwise find nothing forever.
 * Each widget request has an 8s callback timeout with one retry, then degrades to
 * Shopify's own recommendations (same card pipeline) instead of hiding; the same fallback
 * covers the SDK never becoming ready at all.
 *
 * Reporting (required for Fast Simon analytics):
 *  - `RecommendationsProductsShown` — one event per widget carrying that widget's own
 *    products, fired once when the widget scrolls into view;
 *  - `RecommendationProductClicked` — on any product card click, with the widget id, the
 *    1-based position and the source product id.
 * Both go through `fsEvent`, which resolves the event name against the enum the loaded SDK
 * build actually exposes (same pattern as fs-sdk-results.js).
 *
 * Performance notes (per the repo's performance rules):
 *  - no scroll listeners, no document-wide DOM scans; the widget fetch waits for an
 *    IntersectionObserver (400px rootMargin — the same lazy point the native
 *    product-recommendations element used);
 *  - `withAttributes: false` is passed explicitly — the store's global SDK config sets it to
 *    true, which costs latency we do not need here because the cards come from Liquid;
 *  - the widget request itself is issued at most once per element.
 */
(() => {
  'use strict';

  /** Shopify renders at most 20 `all_products` lookups per template render. */
  const CARD_CHUNK_SIZE = 20;
  /** First token of `q` that tells fs-search-cards.liquid this is a card request. */
  const CARD_TOKEN = 'fscardsrender';
  /**
   * sessionStorage key shared with fs-sdk-results.js: the "Fast Simon search cards" section
   * lives on the search template, so its generated id is unknowable from product Liquid. The
   * results surface harvests it once per session; reusing the same key means the product page
   * never pays the whole-page discovery cost when the shopper came from a listing.
   */
  const CARDS_ID_KEY = 'fs-cards-section-id';

  /** Bounded SDK-readiness poll: SDK_WAIT_TRIES × 500ms before the widgets are hidden. */
  const SDK_WAIT_TRIES = 60;
  /** A widget request unanswered for this long is treated as failed and retried once. */
  const RECOMMENDATION_TIMEOUT_MS = 8000;

  /** Bounds both the network request and response-body read. */
  const fetchBody = async (url, accept, format) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), RECOMMENDATION_TIMEOUT_MS);
    try {
      const response = await fetch(url, {
        headers: { Accept: accept },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`recommendation fetch ${response.status}`);
      return format === 'json' ? await response.json() : await response.text();
    } finally {
      clearTimeout(timer);
    }
  };

  const safeStorage = (run) => {
    try {
      return run();
    } catch (error) {
      return ''; // throws in private mode; a whole-page response works without it
    }
  };

  const chunk = (items, size) => {
    const out = [];
    for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
    return out;
  };

  const handleFromUrl = (url) => {
    const match = /\/products\/([^/?#]+)/.exec(url || '');
    return match ? match[1] : null;
  };

  /**
   * Reports an event by the first name the loaded SDK build actually exposes. The
   * recommendations events are documented, but this store's build has lagged the docs
   * before, so each case may list candidates in order of preference and fall back to one
   * the build has rather than dropping the data.
   *
   * `reportAppType` forces an immediate send (the "SPA" app type) for actions that happen in
   * place with no navigation — a quick view — so the report is not deferred to the next page
   * initialization under this store's MPA config.
   *
   * @param {string|string[]} name one event name, or names in order of preference
   * @param {object} data
   * @param {string} [reportAppType] optional AppType override ("SPA" | "MPA")
   */
  const fsEvent = (name, data, reportAppType) => {
    const names = window.FastSimonEventName;
    if (!names || typeof window.FastSimonSDK?.event !== 'function') return;
    const candidate = (Array.isArray(name) ? name : [name]).find((n) => names[n] != null);
    if (!candidate) return;
    try {
      const report = { eventName: names[candidate], data };
      if (reportAppType) report.reportAppType = reportAppType;
      window.FastSimonSDK.event(report);
    } catch (error) {
      console.warn('[Fast Simon] event failed', candidate, error);
    }
  };

  /* ------------------------------------------------------------ card fetching */

  /**
   * The cards section's generated id, or '' when it is not known yet. Discovered from the
   * marker the section renders (only visible on /search), then from the session cache the
   * results surface fills; until then fetchCards() requests the full /search page and reads
   * the `<li>` elements out of that — same cards, more bytes, once per session at most.
   */
  let cardsSectionId = (() => {
    const marker = document.querySelector('[data-fs-cards-section]');
    const wrapper = marker && marker.closest('.shopify-section');
    if (wrapper && wrapper.id) return wrapper.id.replace(/^shopify-section-/, '');
    return safeStorage(() => window.sessionStorage.getItem(CARDS_ID_KEY));
  })();

  const searchUrl = `${window.Shopify?.routes?.root || '/'}search`;

  /** Caches the id learned from a whole-page response so later widgets get a cheap fragment. */
  const rememberCardsSectionId = (doc) => {
    if (cardsSectionId) return;
    const marker = doc.querySelector('[data-fs-cards-section]');
    const wrapper = marker && marker.closest('.shopify-section');
    if (!wrapper || !wrapper.id) return;
    cardsSectionId = wrapper.id.replace(/^shopify-section-/, '');
    safeStorage(() => window.sessionStorage.setItem(CARDS_ID_KEY, cardsSectionId));
  };

  /**
   * Ask Liquid to render native product cards for `handles`.
   * `type=article` keeps Shopify's own (unused) search cheap; only `search.terms` is read.
   * With a known section id this returns just the fragment; without one it returns the whole
   * /search page and the cards are read out of that (see cardsSectionId above).
   *
   * @param {string[]} handles
   * @returns {Promise<Map<string, HTMLLIElement>>}
   */
  const fetchCards = async (handles) => {
    const byHandle = new Map();
    if (!handles.length) return byHandle;

    const requests = chunk(handles, CARD_CHUNK_SIZE).map(async (group) => {
      const groupCards = new Map();
      const q = `${CARD_TOKEN} ${group.join(' ')}`;

      const fetchChunk = async (sectionId) => {
        const sectionParam = sectionId ? `&section_id=${encodeURIComponent(sectionId)}` : '';
        const url = `${searchUrl}?q=${encodeURIComponent(q)}&type=article${sectionParam}`;
        const html = await fetchBody(url, 'text/html', 'text');
        return new DOMParser().parseFromString(html, 'text/html');
      };

      const collect = (doc) => {
        for (const li of doc.querySelectorAll('li.product-grid__item')) {
          const handle = li.dataset.productHandle;
          if (handle && group.includes(handle)) groupCards.set(handle, li);
        }
        rememberCardsSectionId(doc);
      };

      const requestedSectionId = cardsSectionId;
      let doc;
      try {
        doc = await fetchChunk(requestedSectionId);
      } catch (error) {
        if (!requestedSectionId) throw error;
        // A missing section can also respond with a non-success HTTP status.
      }
      if (doc) collect(doc);

      /*
       * A stale id — the merchant renamed or re-added the fs-search-cards section, or a
       * cached sessionStorage value points at one that is gone — renders nothing rather than
       * a widget. Fall back to the whole page, which relearns the id from the marker.
       */
      if (requestedSectionId && (!doc || !doc.querySelector('[data-fs-cards-section]'))) {
        if (cardsSectionId === requestedSectionId) {
          cardsSectionId = '';
          safeStorage(() => window.sessionStorage.removeItem(CARDS_ID_KEY));
        }
        collect(await fetchChunk(''));
      }
      return groupCards;
    });

    for (const cards of await Promise.all(requests)) {
      for (const [handle, card] of cards) byHandle.set(handle, card);
    }
    return byHandle;
  };

  /* -------------------------------------------------------------- card scripts */

  /**
   * Re-runs inline <script> tags that arrived inside injected cards. Cards are parsed with
   * DOMParser, and a parser-created script is flagged "already started" — moving it into the
   * document never executes it. The theme relies on one such script: the
   * `[data-product-map-hover-swatch]` handler in snippets/variant-swatches.liquid that swaps
   * the card's primary image when a colour swatch is hovered.
   *
   * Only executable scripts are re-created — the per-card `application/json` variant blobs
   * are data and must stay exactly where they are. Bodies are deduplicated for the lifetime
   * of the page, so the desktop and mobile copies of the same card run the handler once.
   *
   * @param {ParentNode} root
   */
  const EXECUTABLE_SCRIPT_TYPES = new Set(['', 'text/javascript', 'application/javascript', 'module']);
  const executedScripts = new Set();

  const runInjectedScripts = (root) => {
    for (const original of root.querySelectorAll('script')) {
      const type = (original.getAttribute('type') || '').trim().toLowerCase();
      if (!EXECUTABLE_SCRIPT_TYPES.has(type)) continue; // JSON data blobs etc. — leave alone

      const key = original.src || original.textContent;
      if (!key) continue;

      if (executedScripts.has(key)) {
        original.remove(); // already running from an earlier render
        continue;
      }
      executedScripts.add(key);

      const replacement = document.createElement('script');
      for (const { name, value } of original.attributes) replacement.setAttribute(name, value);
      if (!original.src) replacement.textContent = original.textContent;
      original.replaceWith(replacement);
    }
  };

  /* ------------------------------------------------------------------- mount */

  /** Marks an element as mounted so repeated scans and editor re-renders skip it. */
  const MOUNTED_FLAG = 'fsMounted';

  /**
   * Builds the `.resource-list__item` wrappers the native recommendation list uses. The
   * fetched `<li class="product-grid__item">` exists only to carry the card through the
   * Section Rendering API — its contents move into the wrapper, and the Fast Simon
   * attribution (widget id, 1-based position inside that widget) rides on the wrapper.
   *
   * @param {HTMLLIElement} card
   * @param {string} widgetID
   * @param {number} position
   */
  const buildItem = (card, widgetID, position) => {
    const item = document.createElement('div');
    item.className = 'resource-list__item';
    item.dataset.productId = card.dataset.productId || '';
    item.dataset.fsWidgetId = widgetID;
    item.dataset.fsPosition = String(position);
    while (card.firstChild) item.appendChild(card.firstChild);
    return item;
  };

  /**
   * Places the built items into every list the widget rendered (a desktop list, and a
   * mobile carousel list when "carousel on mobile" is on). Carousel lists arrive from
   * Liquid with one empty `slideshow-slide` per configured product, so the slide markup,
   * the timeline scopes and the arrows all stay theme-owned; filling them and dropping
   * the unused ones is all this does.
   *
   * The slideshow is only re-initialized AFTER the list is visible again. Its async
   * connectedCallback measures the scroller, and initializing against a [hidden]
   * (display:none) container leaves it with zero-size slides and a visible-slide count its
   * ResizeObserver never repairs (it only recalculates for 2+ visible slides) — which
   * showed up as a PDP that jammed while scrolling. So: fill, drop the empty slides (refs
   * re-resolve on reconnect), unhide, then re-insert the element on the frames after the
   * browser has laid the list out.
   *
   * @param {HTMLElement[]} lists
   * @param {HTMLElement[]} items
   */
  const fillLists = (lists, items) => {
    lists.forEach((list) => {
      // Only fill the recommendation carousel's outer slides, not nested placeholders
      // or the image-gallery slides inside product cards.
      const slides = [...list.querySelectorAll('slideshow-slide')].filter((slide) => {
        const ancestorSlide = slide.parentElement?.closest('slideshow-slide');
        return !ancestorSlide || !list.contains(ancestorSlide);
      });
      let staleSlideshow = null;

      if (slides.length) {
        // Replace the empty placeholder contents instead of appending alongside them.
        items.forEach((item, index) => slides[index]?.replaceChildren(item.cloneNode(true)));
        // Drop the unused pre-rendered slides outright rather than hiding them: the
        // slideshow resolves its slide refs at connect, and a merely-hidden slide would
        // still be reachable by the arrows after the re-init below.
        for (let index = items.length; index < slides.length; index += 1) {
          slides[index].remove();
        }
        staleSlideshow = list.querySelector('slideshow-component');
      } else {
        list.replaceChildren(...items.map((item) => item.cloneNode(true)));
      }

      // Must run after insertion: a re-created script only executes once it is in the document.
      runInjectedScripts(list);
      list.hidden = false;

      if (staleSlideshow) {
        // Re-insert the element itself so it disconnects and reconnects — a fresh
        // Scroller, ResizeObserver and slide refs — but only once the now-visible list
        // has been laid out; never against display:none.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (staleSlideshow.isConnected) staleSlideshow.replaceWith(staleSlideshow);
          });
        });
      }
    });
  };

  /** Hides the whole widget (skeleton included) — Fast Simon returned nothing or failed. */
  const markEmpty = (root) => {
    root.dataset.fsStatus = 'empty';
  };

  /**
   * One `productRecommendationByWidget` call as a promise.
   *
   * The SDK's callback is its only success path, and the SDK is only guaranteed to behave
   * from its `onReady` (initialization finished) onward — a call that races initialization
   * or loses a network round-trip can go unanswered forever, which used to leave the
   * widget skeleton on the page indefinitely. Rejecting on a timeout turns that hang into
   * a retry.
   *
   * @param {string} sourceProductID
   * @param {string[]} widgetIds
   * @param {number} [timeoutMs]
   * @returns {Promise<object>} the SDK callback's response
   */

  const requestRecommendations = (sourceProductID, widgetIds, timeoutMs = RECOMMENDATION_TIMEOUT_MS) =>
    new Promise((resolve, reject) => {
      let settled = false;
      const finish = (fn, value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        fn(value);
      };
      const timer = setTimeout(
        () => finish(reject, new Error('recommendation callback timed out')),
        timeoutMs
      );
      try {
        window.FastSimonSDK.productRecommendationByWidget({
          productID: sourceProductID,
          widgetsIDS: widgetIds,
          withAttributes: false, // cards come from Liquid; attributes would only cost latency
          callback: (response) => finish(resolve, response),
        });
      } catch (error) {
        finish(reject, error);
      }
    });

  /**
   * Shopify's own recommendations, fetched as JSON and rendered through the same card
   * pipeline. Degradation path: Fast Simon legitimately returns nothing for some
   * products/sessions (cold-start personalization, not-yet-synced products), and the SDK
   * may never become ready at all (blocked CDN) — in both cases the shopper still gets a
   * widget. Attribution degrades: these products carry no widget id, which the Fast Simon
   * reports filter out, but a visible widget beats an empty one.
   *
   * @param {string} sourceProductID
   * @param {string} intent 'related' or 'complementary' ('' → the platform default)
   * @param {number} limit
   * @returns {Promise<Array<{id: string, handle: string}>>}
   */
  const fetchNativeFallback = async (sourceProductID, intent, limit) => {
    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(sourceProductID)}&limit=${Math.min(10, Math.max(1, limit))}${intent ? `&intent=${encodeURIComponent(intent)}` : ''
      }`;
    const data = await fetchBody(url, 'application/json', 'json');
    return (Array.isArray(data.products) ? data.products : []).map((product) => ({
      id: String(product.id),
      handle: product.handle,
    }));
  };

  /**
   * Mounts one `<fs-recommendations>` element: waits until it is near the viewport, asks
   * Fast Simon for the configured widgets, renders the theme's cards for whatever came
   * back and wires the reporting events. When Fast Simon yields nothing (or, with
   * skipSdk, never became ready), Shopify's own recommendations are rendered instead.
   *
   * @param {HTMLElement} root
   * @param {{skipSdk?: boolean}} [options] skipSdk mounts straight to the Shopify fallback
   */
  const mount = (root, options = {}) => {
    root.dataset[MOUNTED_FLAG] = '1';
    const skipSdk = options.skipSdk === true;

    const sourceProductID = root.dataset.sourceProductId || '';
    const widgetIds = (root.dataset.widgetIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    const configuredMax = Number(root.dataset.maxProducts);
    const maxProducts = Number.isInteger(configuredMax) && configuredMax > 0 ? configuredMax : 4;
    /** 'related' | 'complementary' — the intent for the Shopify fallback when used. */
    const fallbackIntent = ['related', 'complementary'].includes(root.dataset.fallbackIntent)
      ? root.dataset.fallbackIntent
      : '';

    const lists = [...root.querySelectorAll('[data-fs-list]')];
    // Widget ids are only required when Fast Simon is the source; the Shopify fallback
    // (skipSdk) renders without them.
    if (!sourceProductID || !lists.length || (!skipSdk && !widgetIds.length)) {
      markEmpty(root); // misconfigured — never leave a skeleton on the page
      return;
    }

    // Click attribution: one delegated listener per widget. Colour swatches are separate
    // products on this store and are handled by the theme's own card script, so they are
    // excluded here (same rule as the results controller).
    root.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;
        if (event.target.closest('a[data-mapped-product-url]')) return;

        // A multi-variant card's quick-add button opens the product in a modal instead of
        // navigating — that is a quick view, its own event, not a click-through to the product.
        // Reported as "SPA" because the modal opens in place (no navigation), so it is sent now
        // rather than deferred to the next page init under this store's MPA config.
        const quickView = event.target.closest('.quick-add__button--choose');
        if (quickView) {
          const qvItem = quickView.closest('.resource-list__item[data-fs-widget-id]');
          if (qvItem && root.contains(qvItem) && qvItem.dataset.fsWidgetId) {
            fsEvent(
              ['QuickViewClickedWidget', 'quickViewWidget'],
              {
                widget_id: qvItem.dataset.fsWidgetId,
                productID: qvItem.dataset.productId,
                position: Number(qvItem.dataset.fsPosition) || 1,
              },
              'SPA'
            );
          }
          return; // a preview is not a click-through — do not also fire the product-click event
        }

        const item = event.target.closest('.resource-list__item[data-fs-widget-id]');
        if (!item || !root.contains(item) || !item.dataset.fsWidgetId) return;

        // A product click means the shopper navigated to the product, so mirror the theme's own
        // navigation rule (product-card.js). The theme binds navigation to the <product-card>
        // element, so a click only navigates when it lands inside one. Gate on that FIRST: a click
        // on the .resource-list__item wrapper's own space — outside the <product-card> it holds —
        // never triggers the theme's handler, so it must not be reported either. Inside the card,
        // apply the theme's exact predicate: it navigates unless the click is on an interactive
        // control other than the main link. This excludes the single-variant quick-add button (its
        // add is reported by the cart controller) and the gallery/slideshow arrows, which are not
        // shopper navigation. The multi-variant quick-add button was already handled above as a
        // quick view.
        const productCard = event.target.closest('product-card');
        if (!productCard || !item.contains(productCard)) return;
        const interactive = event.target.closest('button, input, label, select, a, [tabindex="1"]');
        const mainLink = event.target.closest('a[ref="productCardLink"]');
        if (interactive && interactive !== mainLink) return;

        fsEvent('RecommendationProductClicked', {
          productID: item.dataset.productId,
          widget_id: item.dataset.fsWidgetId,
          position: Number(item.dataset.fsPosition) || 1,
          sourceProductID,
        });
      },
      { passive: true }
    );

    let rendered = false;

    /** Flattens a Fast Simon response into display order, capped at maxProducts. */
    const planFromResponse = (response) => {
      const planned = [];
      const seenByWidget = new Map();
      collect: for (const widget of Array.isArray(response?.payload) ? response.payload : []) {
        if (!widget) continue;
        const widgetID = String(widget.widgetID || '');
        const seen = seenByWidget.get(widgetID) || new Set();
        seenByWidget.set(widgetID, seen);
        for (const product of Array.isArray(widget.payload) ? widget.payload : []) {
          const handle = handleFromUrl(product?.u);
          if (!handle || String(product?.id || '') === sourceProductID || seen.has(handle)) continue;
          if (planned.length >= maxProducts) break collect;
          seen.add(handle);
          planned.push({ widget, product });
        }
      }
      return planned;
    };

    /**
     * Fast Simon plan (products + theme cards), or null when it yields nothing usable
     * after one retry. An unanswered callback races initialization; an empty payload can
     * be that same race; the card fetch is a plain network round-trip.
     */
    const loadFastSimonPlan = async () => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const response = await requestRecommendations(sourceProductID, widgetIds);
          const planned = planFromResponse(response);
          if (!planned.length) throw new Error('no recommendation products returned');

          const handles = [...new Set(planned.map(({ product }) => handleFromUrl(product.u)).filter(Boolean))];
          const cards = await fetchCards(handles);
          if (cards.size) return { planned, cards };
          throw new Error('no theme cards for the recommended products');
        } catch (error) {
          if (attempt === 1) {
            console.warn('[Fast Simon] recommendations failed; using Shopify fallback', error);
            return null;
          }
          await new Promise((done) => setTimeout(done, 500));
        }
      }
      return null;
    };

    /** Shopify's own recommendations through the same card pipeline, or null. */
    const loadNativePlan = async () => {
      try {
        const products = await fetchNativeFallback(sourceProductID, fallbackIntent, maxProducts);
        // No widget id: attribution degrades to nothing for these products, but the
        // shopper always gets recommendations instead of an empty widget.
        const planned = products
          .filter((product) => product.handle && product.id !== sourceProductID)
          .slice(0, maxProducts)
          .map((product) => ({
            widget: { widgetID: '' },
            product: { id: product.id, u: `/products/${product.handle}` },
          }));
        if (!planned.length) return null;

        const handles = planned.map(({ product }) => handleFromUrl(product.u));
        const cards = await fetchCards(handles);
        if (!cards.size) return null;
        return { planned, cards };
      } catch (error) {
        console.warn('[Fast Simon] Shopify recommendations fallback failed', error);
        return null;
      }
    };

    const load = async () => {
      if (rendered) return;
      rendered = true;

      const plan = (skipSdk ? null : await loadFastSimonPlan()) || (await loadNativePlan());
      if (!plan) {
        markEmpty(root); // neither source produced anything — hide rather than skeleton
        return;
      }
      if (!root.isConnected) return;
      const { planned, cards } = plan;
      // Do not report products that cannot fit in the theme's existing slide slots.
      const slideCapacities = lists
        .map((list) => [...list.querySelectorAll('slideshow-slide')].filter((slide) => {
          const ancestorSlide = slide.parentElement?.closest('slideshow-slide');
          return !ancestorSlide || !list.contains(ancestorSlide);
        }).length)
        .filter((count) => count > 0);
      const renderLimit = slideCapacities.length ? Math.min(...slideCapacities) : maxProducts;

      /** One entry per widget that actually displayed products, for the shown event. */
      const renderedByWidget = new Map();
      const positionByWidget = new Map();
      const items = [];

      for (const { widget, product } of planned) {
        if (items.length >= renderLimit) break;
        const card = cards.get(handleFromUrl(product.u));
        if (!card) continue; // handle unpublished or not found — skip rather than leave a hole

        const widgetID = String(widget.widgetID || '');
        const position = (positionByWidget.get(widgetID) || 0) + 1;
        positionByWidget.set(widgetID, position);
        items.push(buildItem(card.cloneNode(true), widgetID, position));

        const entry = renderedByWidget.get(widgetID) || { widget, products: [] };
        entry.products.push(product);
        renderedByWidget.set(widgetID, entry);
      }

      if (!items.length) {
        markEmpty(root);
        return;
      }

      fillLists(lists, items);
      root.querySelector('[data-fs-skeleton]')?.remove();
      root.dataset.fsStatus = 'ready';

      // The shown event fires once, when the products are actually visible — not when the
      // data merely arrived. One event per widget, carrying that widget's own products.
      const observer = new IntersectionObserver(
        (entries, obs) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          obs.disconnect();
          for (const { widget, products } of renderedByWidget.values()) {
            if (!String(widget.widgetID || '')) continue;
            fsEvent('RecommendationsProductsShown', {
              products,
              widget_id: String(widget.widgetID || ''),
              id: sourceProductID,
            });
          }
        },
        { rootMargin: '0px' }
      );
      observer.observe(root);
    };

    // Lazy like the native widget: nothing is requested until the section is near the
    // viewport. In the theme editor the widget sits in the preview, which intersects too.
    const loader = new IntersectionObserver(
      (entries, obs) => {
        if (!entries[0]?.isIntersecting) return;
        obs.disconnect();
        load().catch((error) => {
          console.warn('[Fast Simon] recommendations failed', error);
          markEmpty(root);
        });
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    loader.observe(root);
  };

  /** Mounts every not-yet-mounted `<fs-recommendations>` element on the page. */
  const scan = () => {
    for (const root of document.querySelectorAll('fs-recommendations')) {
      if (root.dataset[MOUNTED_FLAG] === '1') continue;
      mount(root);
    }
  };

  /* --------------------------------------------------------------- bootstrap */

  /**
   * Boots once.
   *
   * The SDK is only guaranteed to behave from its `onReady` (initialization finished)
   * onward — the `window.FastSimonSDK` symbol exists before that, and calls made in the
   * gap can be dropped without ever firing their callback (the "widget sometimes never
   * loads" bug). So readiness is: `window.__fastSimonSdkReady`, set by the theme's
   * onReady (layout/theme.liquid), reached either immediately (this script ran after init
   * — e.g. a theme editor re-render) or by chaining this start onto the config's onReady
   * when the SDK is still on its way. A bounded poll is the last resort, and gives up by
   * hiding the widgets instead of leaving skeletons if the SDK never becomes ready
   * (blocked CDN, removed config).
   */
  let started = false;
  const startOnce = () => {
    if (started) return;
    started = true;
    scan();

    // A parser-inserted script executes mid-parse, BEFORE the <fs-recommendations>
    // element it serves (below it) exists — an immediate scan finds nothing, and this
    // IIFE runs only once per script execution. When the SDK is already ready at that
    // moment (fast, cached load) nothing else would ever scan again and the widget
    // silently never mounted. Rescan at DOMContentLoaded to close that ordering gap;
    // scan() is idempotent, so already-mounted widgets are skipped.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        scan();
      }, { once: true });
    }
  };

  /**
   * The SDK never became ready (blocked CDN, config removed). The widgets still render —
   * through Shopify's own recommendations — instead of hiding; attribution degrades.
   */
  const giveUp = () => {
    console.warn('[Fast Simon] SDK not ready in time; using Shopify recommendations');
    const scanFallback = () => {
      for (const root of document.querySelectorAll('fs-recommendations')) {
        if (root.dataset[MOUNTED_FLAG] === '1') continue;
        mount(root, { skipSdk: true });
      }
    };
    scanFallback();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scanFallback, { once: true });
    }
  };

  if (window.__fastSimonSdkReady) {
    startOnce();
  } else {
    const config = window.__fast_simon_sdk_config;
    if (config && typeof config.onReady === 'function' && !config.__fsRecsHooked) {
      config.__fsRecsHooked = true;
      const onReady = config.onReady;
      config.onReady = function (...args) {
        try {
          if (typeof onReady === 'function') onReady.apply(this, args);
        } finally {
          window.__fastSimonSdkReady = true;
          startOnce();
        }
      };
    }

    let waitTries = 0;
    const poll = () => {
      if (window.__fastSimonSdkReady) return startOnce();
      if (waitTries >= SDK_WAIT_TRIES) return giveUp();
      waitTries += 1;
      setTimeout(poll, 500);
    };
    poll();
  }
})();
